import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { angeboteApi, euro } from "@/lib/angebote"
import "./angebote.css"

/**
 * KI-Angebote (Paul 08.10.2026): Auftrag beschreiben → Entwurf aus aehnlichen
 * frueheren Angeboten des Betriebs. Bearbeiten und Uebergabe nach HERO auf
 * der Detailseite.
 */
export default function AngebotePage() {
  const [tenant, setTenant] = useTenantFilter()
  const [beschreibung, setBeschreibung] = useState("")
  const navigate = useNavigate()
  const qc = useQueryClient()

  const liste = useQuery({
    queryKey: ["angebote", tenant],
    queryFn: () => angeboteApi.liste(tenant),
    enabled: !!tenant,
  })

  const neu = useMutation({
    mutationFn: () => angeboteApi.erstellen(tenant, { beschreibung }),
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ["angebote", tenant] })
      setBeschreibung("")
      navigate(`/angebote/${e.id}?tenant=${encodeURIComponent(tenant)}`)
    },
    onError: (e) => toast(e instanceof Error ? e.message : String(e), "error"),
  })

  const historie = useMutation({
    mutationFn: () => angeboteApi.historieAktualisieren(tenant),
    onSuccess: (r) => {
      toast(`✓ ${r.anzahl} frühere Angebote eingelesen`)
      qc.invalidateQueries({ queryKey: ["angebote", tenant] })
    },
    onError: (e) => toast(e instanceof Error ? e.message : String(e), "error"),
  })

  const h = liste.data?.historie

  return (
    <>
      <PageHeader title="KI-Angebote" />
      <FilterBar tenant={tenant} onTenantChange={setTenant} />
      {!tenant ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Bitte Betrieb wählen.
        </div>
      ) : (
        <div className="ang" style={{ maxWidth: 960 }}>
          <section className="ang-ki">
            <label htmlFor="angebot-beschreibung" className="ang-ki-titel">✨ Neues Angebot – Auftrag beschreiben</label>
            <textarea
              id="angebot-beschreibung"
              data-testid="angebot-beschreibung"
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
              rows={4}
              placeholder="z. B. Bad komplett sanieren, ca. 8 m², bodengleiche Dusche mit Rinne, Wand-WC, Waschtisch 60 cm, Badheizkörper"
              style={{ width: "100%", resize: "vertical" }}
            />
            <div className="ang-neu-zeile">
              <button type="button" className="pp-btn pp-btn-primary" data-testid="angebot-erstellen"
                      disabled={beschreibung.trim().length < 3 || neu.isPending}
                      onClick={() => neu.mutate()}>
                {neu.isPending ? "Entwurf wird erstellt … (bis ~1 Min.)" : "Entwurf erstellen"}
              </button>
              <span data-frei className="ang-klein">
                Grundlage: {h ? `${h.anzahl} frühere Angebote` : "…"} und der HERO-Artikelstamm
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" className="pp-btn pp-btn-sm" disabled={historie.isPending}
                      onClick={() => historie.mutate()}>
                {historie.isPending ? "Lese HERO …" : "Aus HERO neu einlesen"}
              </button>
            </div>
          </section>

          {liste.isLoading ? <Loading label="Lade Entwürfe…" /> :
            liste.isError ? <ErrorBox error={liste.error} /> : (
            <section className="pp-card ang-liste" data-testid="angebot-entwuerfe">
              {(liste.data?.entwuerfe ?? []).length === 0 ? (
                <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>
                  Noch keine Entwürfe. Beschreib oben den ersten Auftrag.
                </div>
              ) : liste.data!.entwuerfe.map((e) => (
                <Link key={e.id} className="ang-entwurf" to={`/angebote/${e.id}?tenant=${encodeURIComponent(tenant)}`}>
                  <span className="ang-entwurf-titel">
                    {e.titel || "Angebot"}
                    {e.ohne_preis > 0 && <span className="pp-badge pp-badge-ringing" style={{ marginLeft: 6 }}>
                      {e.ohne_preis} ohne Preis</span>}
                  </span>
                  <span className="ang-entwurf-summe">{euro(e.summe_netto)}</span>
                  <span className="ang-entwurf-projekt">{e.projekt_name || "ohne Projekt"}</span>
                  <span className="ang-entwurf-datum">
                    {e.status === "in_hero"
                      ? <span className="pp-badge pp-badge-booked">in HERO</span>
                      : <span className="pp-badge pp-badge-done">Entwurf</span>}
                    {" "}{new Date(e.updated_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </Link>
              ))}
            </section>
          )}
        </div>
      )}
    </>
  )
}
